import { Pagination, Stack, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';
import { PagedList } from '../../hooks/usePagedList';

type Props = Pick<PagedList<unknown>, 'page' | 'pageCount' | 'offset' | 'pageSize' | 'total' | 'goToPage'> & {
    /** Overrides the default spacing (e.g. when rendered above a list instead of below it). */
    sx?: SxProps<Theme>;
};

/**
 * Page controls for a `usePagedList` list. Renders nothing when everything fits on one page.
 */
const ListViewPagination = ({ page, pageCount, offset, pageSize, total, goToPage, sx }: Props) => {
    if (pageCount <= 1) {
        return null;
    }

    const rangeStart = offset + 1;
    const rangeEnd = Math.min(offset + pageSize, total);

    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            sx={[{ mt: 2, px: 1 }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            <Typography variant="body2" color="text.secondary">
                {`${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
            </Typography>
            <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(_e: React.ChangeEvent<unknown>, newPage: number) => goToPage(newPage - 1)}
                siblingCount={0}
                boundaryCount={1}
                shape="rounded"
                color="primary"
                size="small"
            />
        </Stack>
    );
};

export default ListViewPagination;
